import { z } from 'zod';
import {
  EGRESS_STRATEGY_VALUES,
  DEFAULT_EGRESS_STRATEGY,
  type EgressStrategy,
} from '@terra/shared-contracts-external-network';

export const DEFAULT_MAX_REDIRECTS = 21;
export const DEFAULT_TOOL_EXECUTION_SOURCE = 'neo';

export const TOOL_EXECUTION_SOURCE_VALUES = [
  'neo',
  'copilot',
  'recon',
  'ai-discovery',
  'authentication-discovery',
  'internal-network-tunnel',
  'crawl',
] as const;

export const ToolExecutionSourceSchema = z.enum(TOOL_EXECUTION_SOURCE_VALUES);
export type ToolExecutionSource = z.infer<typeof ToolExecutionSourceSchema>;

export const HISTORY_SOURCE_VALUES = [
  ...TOOL_EXECUTION_SOURCE_VALUES,
  'proxy',
] as const;
export const HistorySourceSchema = z.enum(HISTORY_SOURCE_VALUES);
export type HistorySource = z.infer<typeof HistorySourceSchema>;

export const EgressStrategySchema = z
  .enum(EGRESS_STRATEGY_VALUES)
  .default(DEFAULT_EGRESS_STRATEGY);
export type { EgressStrategy };

export const RedirectChainEntrySchema = z.object({
  url: z.string(),
  statusCode: z.number(),
  location: z.string(),
});
export type RedirectChainEntry = z.infer<typeof RedirectChainEntrySchema>;

export const TailscaleRoutingSnapshotSchema = z.object({
  networkSlug: z.string().min(1),
  tailscaleNetworkId: z.string().min(1),
  siteId: z.number().int().positive(),
  hasOverlappingCidrs: z.boolean(),
  hasResourceProxy: z.boolean(),
  advertisedRoutes: z.array(z.string().min(1)),
  hostAliases: z.array(
    z.object({
      hostname: z.string().min(1),
      ip: z.string().min(1),
    }),
  ),
  splitDnsDomains: z.array(z.string().min(1)).optional(),
});
export type TailscaleRoutingSnapshot = z.infer<
  typeof TailscaleRoutingSnapshotSchema
>;

export const PROXY_CONTEXT_JWT_TYPE_VALUES = ['user', 'access_key'] as const;
export const ProxyContextJwtTypeSchema = z.enum(PROXY_CONTEXT_JWT_TYPE_VALUES);
export type ProxyContextJwtType = z.infer<typeof ProxyContextJwtTypeSchema>;

export const ProxyContextSchema = z.object({
  contextId: z.string(),
  toolCallId: z.string(),
  parentToolCallId: z.string().optional(),
  threadId: z.string(),
  sessionId: z.string(),
  executionId: z.string().optional(),
  applicationId: z.string(),
  applicationUrl: z.string().optional(),
  tenantId: z.string(),
  tailscaleNetworkId: z.string().optional(),
  tailscaleRouting: TailscaleRoutingSnapshotSchema.optional(),
  twingateNetworkId: z.string().optional(),
  isInternalApp: z.boolean(),
  egressStrategy: EgressStrategySchema,
  credentialId: z.string().optional(),
  proxyCredentialId: z.string().optional(),
  userId: z.string().optional(),
  jwtType: ProxyContextJwtTypeSchema.optional(),
  source: ToolExecutionSourceSchema.default(DEFAULT_TOOL_EXECUTION_SOURCE),
  executingCloudService: z.string().min(1).optional(),
  toolType: z.enum(['HTTP', 'PYTHON']).default('PYTHON'),
  toolName: z.string(),
  registeredAt: z.number(),
  maxRedirects: z.number().optional(),
  persistCookieJar: z.boolean().optional(),
  requestsPerMinute: z.number().int().positive().optional(),
});

export type ProxyContextDto = z.infer<typeof ProxyContextSchema>;
export type ProxyContextInput = z.input<typeof ProxyContextSchema>;

export const GIT_PROXY_APPLICATION_ID = 'git-integration';
export const GIT_PROXY_TOOL_NAME = 'git-integration';
export const WEB_APP_SCANNER_PROXY_TOOL_NAME = 'web-app-scanner';
export const DIRECTORY_SCAN_PROXY_TOOL_NAME = 'directory-scan';
export const HEADLESS_BROWSER_PROXY_TOOL_NAME = 'headless-browser';
export const KATANA_PROXY_TOOL_NAME = 'katana';

export const isGitProxyTraffic = ({
  applicationId,
  toolName,
}: {
  applicationId?: string;
  toolName?: string;
}): boolean =>
  applicationId === GIT_PROXY_APPLICATION_ID ||
  toolName === GIT_PROXY_TOOL_NAME;

export const isWebAppScannerProxyTraffic = ({
  toolName,
}: {
  toolName?: string;
}): boolean => toolName === WEB_APP_SCANNER_PROXY_TOOL_NAME;

export const isDirectoryScanProxyTraffic = ({
  toolName,
}: {
  toolName?: string;
}): boolean => toolName === DIRECTORY_SCAN_PROXY_TOOL_NAME;

export const isUnmeteredProxyTraffic = ({
  toolName,
}: {
  toolName?: string;
}): boolean =>
  toolName === HEADLESS_BROWSER_PROXY_TOOL_NAME ||
  toolName === KATANA_PROXY_TOOL_NAME;

export const isHarCaptureSuppressed = ({
  applicationId,
  toolName,
}: {
  applicationId?: string;
  toolName?: string;
}): boolean =>
  isGitProxyTraffic({ applicationId, toolName }) ||
  isWebAppScannerProxyTraffic({ toolName }) ||
  isDirectoryScanProxyTraffic({ toolName });

export const shouldCaptureHarForStreamedProxyResponse = ({
  applicationId,
  toolName,
}: {
  applicationId?: string;
  toolName?: string;
}): boolean => !isHarCaptureSuppressed({ applicationId, toolName });

export const PROXY_CONTEXT_KEY_PREFIX = 'terra:proxy:ctx';
export const PROXY_CONTEXT_TTL_SECONDS = 300;
export const BROWSER_PROXY_CONTEXT_TTL_SECONDS = 15 * 60;
export const REDIRECT_CHAIN_KEY_PREFIX = 'terra:proxy:redirect-chain';
export const REDIRECT_CHAIN_TTL_SECONDS = 300;
export const AUTH_COOKIE_JAR_KEY_PREFIX = 'terra:proxy:auth-cookie-jar';
export const AUTH_COOKIE_JAR_TTL_SECONDS = 900;
export const GLOBAL_HEADERS_PROXY_CACHE_PREFIX = 'proxy:global-headers-v2';
export const GLOBAL_HEADERS_PROXY_CACHE_TTL_SECONDS = 600;
export const GLOBAL_HEADERS_PROXY_CACHE_MISS_SENTINEL =
  '__proxy:global-headers:none__';

export const getGlobalHeadersProxyCacheKey = (applicationId: string): string =>
  `${GLOBAL_HEADERS_PROXY_CACHE_PREFIX}:${applicationId}`;

export function buildAuthCookieJarCacheKey({
  sessionId,
  credentialId,
}: {
  sessionId: string;
  credentialId: string;
}): string {
  return `${AUTH_COOKIE_JAR_KEY_PREFIX}:${sessionId}:${credentialId}`;
}
